import { apiClient } from './client'
import type {
  FeatureFlag,
  FeatureFlagCreateInput,
  FeatureFlagUpdateInput,
  FeatureFlagTargets,
  FeatureFlagFilters,
  FeatureFlagListResponse,
} from '../types/feature'

/**
 * List all feature flags with optional filters
 */
export async function listFeatureFlags(
  filters?: FeatureFlagFilters
): Promise<FeatureFlagListResponse> {
  const response = await apiClient.get<FeatureFlagListResponse>('/features', {
    params: filters,
  })
  return response.data
}

/**
 * Get a single feature flag by ID
 */
export async function getFeatureFlagById(id: string): Promise<{ data: FeatureFlag }> {
  const response = await apiClient.get<{ data: FeatureFlag }>(`/features/${id}`)
  return response.data
}

/**
 * Get a feature flag by key
 */
export async function getFeatureFlagByKey(feature_key: string): Promise<{ data: FeatureFlag }> {
  const response = await apiClient.get<{ data: FeatureFlag }>(`/features/key/${feature_key}`)
  return response.data
}

/**
 * Create a new feature flag
 */
export async function createFeatureFlag(
  data: FeatureFlagCreateInput
): Promise<{ data: FeatureFlag }> {
  const response = await apiClient.post<{ data: FeatureFlag }>('/features', data)
  return response.data
}

/**
 * Update a feature flag
 */
export async function updateFeatureFlag(
  id: string,
  data: FeatureFlagUpdateInput
): Promise<{ data: FeatureFlag }> {
  const response = await apiClient.patch<{ data: FeatureFlag }>(`/features/${id}`, data)
  return response.data
}

/**
 * Delete a feature flag
 */
export async function deleteFeatureFlag(id: string): Promise<void> {
  await apiClient.delete(`/features/${id}`)
}

/**
 * Get targets for a feature flag
 */
export async function getFeatureFlagTargets(id: string): Promise<{ data: FeatureFlagTargets }> {
  const response = await apiClient.get<{ data: FeatureFlagTargets }>(`/features/${id}/targets`)
  return response.data
}

/**
 * Add targets to a feature flag
 */
export async function addFeatureFlagTargets(
  id: string,
  targetIds: string[]
): Promise<{ data: FeatureFlag }> {
  const response = await apiClient.post<{ data: FeatureFlag }>(`/features/${id}/targets`, {
    targetIds,
  })
  return response.data
}

/**
 * Remove targets from a feature flag
 */
export async function removeFeatureFlagTargets(
  id: string,
  targetIds: string[]
): Promise<{ data: FeatureFlag }> {
  const response = await apiClient.delete<{ data: FeatureFlag }>(`/features/${id}/targets`, {
    data: { targetIds },
  })
  return response.data
}

/**
 * Set target state (enable/disable)
 */
export async function setFeatureFlagTargetState(
  id: string,
  targetId: string,
  enabled: boolean
): Promise<{ data: FeatureFlag }> {
  const response = await apiClient.put<{ data: FeatureFlag }>(
    `/features/${id}/targets/${targetId}`,
    { enabled }
  )
  return response.data
}
